import React, { useState, useEffect } from 'react';
import TouchButton from '../ui/TouchButton';
import { TouchInput, TouchFormGroup } from '../ui/TouchForm';

const DistanceFilter = ({ 
  radius,
  onRadiusChange,
  centerPoint,
  onCenterPointChange,
  userLocation
}) => {
  const [customLocation, setCustomLocation] = useState('');
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);

  const distancePresets = [
    { value: 250, label: '250m', icon: '🏃‍♂️', description: 'Walking distance' },
    { value: 500, label: '500m', icon: '🚶‍♂️', description: 'Short walk' },
    { value: 1000, label: '1km', icon: '🚴‍♂️', description: 'Biking distance' },
    { value: 2000, label: '2km', icon: '🛴', description: 'Nearby area' },
    { value: 5000, label: '5km', icon: '🚗', description: 'Driving distance' },
    { value: 10000, label: '10km', icon: '🚙', description: 'Extended area' },
    { value: 25000, label: '25km', icon: '🚐', description: 'Wide area' },
    { value: 50000, label: '50km', icon: '🛻', description: 'Regional' }
  ];

  const getCurrentLocation = async () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by this browser');
      return;
    }

    setIsLoadingLocation(true);
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000
        });
      });

      const location = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy
      };

      onCenterPointChange(location);
    } catch (error) {
      console.error('Location error:', error);
      alert('Unable to get your location: ' + error.message);
    } finally {
      setIsLoadingLocation(false);
    }
  };

  const searchCustomLocation = async () => {
    if (!customLocation.trim()) return;

    setIsLoadingLocation(true);
    try {
      // Using OpenStreetMap Nominatim for geocoding
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(customLocation)}&limit=1`
      );
      
      const data = await response.json();
      
      if (data && data.length > 0) {
        const location = {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon),
          name: data[0].display_name
        };
        
        onCenterPointChange(location);
        setCustomLocation('');
      } else {
        alert('Location not found. Please try a different search term.');
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      alert('Failed to search for location. Please try again.');
    } finally {
      setIsLoadingLocation(false);
    }
  };

  const formatDistance = (meters) => {
    if (meters < 1000) {
      return `${meters}m`;
    } else {
      return `${(meters / 1000).toFixed(1)}km`;
    }
  };

  const getDistanceDescription = (meters) => {
    if (meters <= 500) return 'Very close';
    if (meters <= 1000) return 'Walking distance';
    if (meters <= 2000) return 'Short bike ride';
    if (meters <= 5000) return 'Quick drive';
    if (meters <= 10000) return 'Nearby';
    if (meters <= 25000) return 'Extended area';
    return 'Wide area';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Center Point Selection */}
      <TouchFormGroup>
        <label style={{ 
          fontSize: '14px', 
          fontWeight: '600', 
          marginBottom: '12px',
          display: 'block'
        }}>
          📍 Search Center:
        </label>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {/* Current Location Button */}
          <TouchButton
            onClick={getCurrentLocation}
            disabled={isLoadingLocation}
            variant={centerPoint === userLocation ? 'primary' : 'outline'}
            size="small"
            style={{ 
              fontSize: '14px',
              justifyContent: 'flex-start',
              gap: '8px'
            }}
          >
            {isLoadingLocation ? (
              <>
                <span>🔄</span>
                <span>Getting location...</span>
              </>
            ) : (
              <>
                <span>📍</span>
                <span>Use Current Location</span>
              </>
            )}
          </TouchButton>
          
          {/* Custom Location Search */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <TouchInput
              placeholder="Enter city, address, or landmark..."
              value={customLocation}
              onChange={(e) => setCustomLocation(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  searchCustomLocation();
                }
              }}
              style={{ flex: 1 }}
            />
            <TouchButton
              onClick={searchCustomLocation}
              disabled={!customLocation.trim() || isLoadingLocation}
              size="small"
            >
              🔍
            </TouchButton>
          </div>
          
          {/* Current Center Point Display */}
          {centerPoint && (
            <div style={{
              padding: '8px',
              backgroundColor: '#e8f5e8',
              borderRadius: '6px',
              fontSize: '12px',
              color: '#2E7D32'
            }}>
              <strong>Search center:</strong>{' '}
              {centerPoint.name || 
               (centerPoint === userLocation ? 'Your current location' : 
                `${centerPoint.lat.toFixed(4)}, ${centerPoint.lng.toFixed(4)}`)}
            </div>
          )}
        </div>
      </TouchFormGroup>

      {/* Distance Radius Selection */}
      <TouchFormGroup>
        <label style={{ 
          fontSize: '14px', 
          fontWeight: '600', 
          marginBottom: '12px',
          display: 'block'
        }}>
          📏 Search Radius:
        </label>
        
        {/* Distance Preset Buttons */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
          gap: '8px',
          marginBottom: '16px'
        }}>
          {distancePresets.map(preset => (
            <TouchButton
              key={preset.value}
              onClick={() => onRadiusChange(radius === preset.value ? null : preset.value)}
              variant={radius === preset.value ? 'primary' : 'outline'}
              size="small"
              style={{ 
                fontSize: '12px',
                flexDirection: 'column',
                gap: '4px',
                height: '60px',
                padding: '8px'
              }}
            >
              <span style={{ fontSize: '16px' }}>{preset.icon}</span>
              <span style={{ fontWeight: '600' }}>{preset.label}</span>
              <span style={{ fontSize: '10px', opacity: 0.8 }}>
                {preset.description}
              </span>
            </TouchButton>
          ))}
        </div>

        {/* Custom Distance Input */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <TouchInput
            type="number"
            placeholder="Custom distance"
            value={radius && !distancePresets.find(p => p.value === radius) ? radius : ''}
            onChange={(e) => onRadiusChange(e.target.value ? parseInt(e.target.value) : null)}
            min="50"
            max="100000"
            style={{ flex: 1 }}
          />
          <select
            style={{
              padding: '8px',
              borderRadius: '6px',
              border: '1px solid #ddd',
              fontSize: '14px'
            }}
            onChange={(e) => {
              const currentValue = radius || 1000;
              const multiplier = e.target.value === 'km' ? 1000 : 1;
              const baseValue = e.target.value === 'km' ? currentValue / 1000 : currentValue;
              onRadiusChange(Math.round(baseValue * multiplier));
            }}
            value={radius >= 1000 ? 'km' : 'm'}
          >
            <option value="m">meters</option>
            <option value="km">kilometers</option>
          </select>
        </div>
      </TouchFormGroup>

      {/* Distance Information */}
      {radius && (
        <div style={{
          padding: '12px',
          backgroundColor: '#e3f2fd',
          borderRadius: '8px',
          border: '1px solid #2196F3'
        }}>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#1976D2', marginBottom: '4px' }}>
            Search Area: {formatDistance(radius)} radius
          </div>
          <div style={{ fontSize: '12px', color: '#333' }}>
            {getDistanceDescription(radius)} • Covers approximately {Math.round(Math.PI * Math.pow(radius / 1000, 2))} km²
          </div>
        </div>
      )}

      {/* Quick Distance Categories */}
      <TouchFormGroup>
        <label style={{ 
          fontSize: '14px', 
          fontWeight: '600', 
          marginBottom: '12px',
          display: 'block'
        }}>
          🎯 Quick Categories:
        </label>
        
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          <TouchButton
            onClick={() => onRadiusChange(500)}
            variant={radius === 500 ? 'primary' : 'outline'}
            size="small"
            style={{ fontSize: '12px' }}
          >
            🏃‍♂️ Walkable
          </TouchButton>
          
          <TouchButton
            onClick={() => onRadiusChange(2000)}
            variant={radius === 2000 ? 'primary' : 'outline'}
            size="small"
            style={{ fontSize: '12px' }}
          >
            🚴‍♂️ Bikeable
          </TouchButton>
          
          <TouchButton
            onClick={() => onRadiusChange(5000)}
            variant={radius === 5000 ? 'primary' : 'outline'}
            size="small"
            style={{ fontSize: '12px' }}
          >
            🚗 Driveable
          </TouchButton>
          
          <TouchButton
            onClick={() => onRadiusChange(25000)}
            variant={radius === 25000 ? 'primary' : 'outline'}
            size="small"
            style={{ fontSize: '12px' }}
          >
            🌍 Regional
          </TouchButton>
        </div>
      </TouchFormGroup>

      {/* Distance Filter Requirements */}
      {!centerPoint && (
        <div style={{
          padding: '12px',
          backgroundColor: '#fff3e0',
          borderRadius: '8px',
          border: '1px solid #FF9800',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#F57C00', marginBottom: '4px' }}>
            📍 Location Required
          </div>
          <div style={{ fontSize: '12px', color: '#333' }}>
            Set a search center to use distance filtering
          </div>
        </div>
      )}

      {/* Clear Distance Filter */}
      {radius && (
        <TouchButton
          onClick={() => onRadiusChange(null)}
          variant="ghost"
          size="small"
          style={{ fontSize: '12px', color: '#666' }}
        >
          Clear Distance Filter
        </TouchButton>
      )}
    </div>
  );
};

export default DistanceFilter;